import { showNotification } from '@helpers/notification-helper';
import { useAppSelector } from '@hooks/app/redux';
import { addWorkspaceWorker, updateWorkspaceWorker } from '@services/external/provider.api';
import { Button, Checkbox, Drawer, Form, Select } from '@ui';
import React from 'react';
import { IWorkspaceWorker } from 'types/workspace.types';
import EmployeesSelect from '../employees-select';
import {
  IDStaffDorm,
  initialValues as baseValues,
  validationSchema,
} from './DepartmentStaffEdit.schema';

const gte = (e: any) => e;

interface DepartmentStaffEditProps {
  visible: boolean;
  onClose?: () => void;
  afterSubmit?: () => void;
  worker?: Partial<IWorkspaceWorker>;
  workspaceId: string;
}

const DepartmentStaffEdit: React.FunctionComponent<DepartmentStaffEditProps> = ({
  visible,
  worker,
  onClose,
  afterSubmit,
  workspaceId,
}) => {
  const providerId = useAppSelector((state) => state.users.currentProvider?.id)!;

  const mode = worker?.id ? 'edit' : 'create';
  const title = mode === 'edit' ? 'Upraviť zamestnanca' : 'Pridať zamestnanca';

  const initialValues = React.useMemo<IDStaffDorm>(() => {
    if (!worker?.id) {
      return { ...baseValues };
    }

    return {
      ...baseValues,
      worker: { value: worker.id!, label: `${worker.firstName} ${worker.lastName}` },
      hasEVisit: worker.hasEVisit ?? false,
    };
  }, [worker]);

  const [, formOptions] = Form.useForm({
    initialValues,
    validationSchema,
    enableReinitialize: true,
    onSubmit: async (v) => {
      try {
        if (mode === 'create') {
          await addWorkspaceWorker(providerId, workspaceId, {
            userId: v.worker?.value!,
            hasEVisit: v.hasEVisit,
          });
        } else {
          await updateWorkspaceWorker(providerId, workspaceId, {
            userId: v.worker?.value!,
            hasEVisit: v.hasEVisit,
          });
        }

        showNotification({ title: 'Operácia prebehla úspešne', copy: '', type: 'SUCCESS' });
        afterSubmit?.();
      } catch (error) {
        showNotification({ title: 'Nepodarilo sa dokončiť operáciu', copy: '', type: 'ERROR' });
      }
    },
  });

  return (
    <Drawer destroyOnClose visible={visible} onClose={onClose} title={title}>
      <Form {...formOptions} showFeedback={false}>
        {mode === 'edit' && initialValues.worker ? (
          <Form.Field name="worker" getValueFromEvent={gte} disabled label="Zamestnanec" required>
            <Select options={[initialValues.worker]} isDisabled />
          </Form.Field>
        ) : (
          <Form.Field name="worker" getValueFromEvent={gte} label="Zamestnanec" required>
            <EmployeesSelect notInWorkspaceId={workspaceId} />
          </Form.Field>
        )}
        <Form.Field name="hasEVisit" valuePropName="checked">
          <Checkbox value={'hasEVisit'}>Má E-termín</Checkbox>
        </Form.Field>
        <Form.Custom>
          {(f) => (
            <Button
              style={{ marginTop: 16 }}
              type="submit"
              loading={f.isSubmitting}
              disabled={!f.isValid}
            >
              {title}
            </Button>
          )}
        </Form.Custom>
      </Form>
    </Drawer>
  );
};

export default React.memo(DepartmentStaffEdit);
